<template>
    <MContainer class="w-full mt-4 items-center justify-center upload-container">
        <div class="w-full">
            <UniversalFilePicker
                class="grow"
                :dropArea="true"
                :selectFilesBtn="true"
                :selectFolderBtn="false"
                :accept="accept"
                :writeToWasmFS="false"
                :filterAssociatedFiles="false"
                :singleSelection="true"
                :embedded="true"
                :showTypeColumn="false"
                :hideTreeWhenNoFolders="true"
                :showAccept="true"
                @uploadFiles="onUploadFiles" />
        </div>
        <MContainer v-if="iuState === UIState.UPLOADING" class="upload-overlay items-center justify-center">
            <ProgressSpinner style="width: 150px; height: 150px" animationDuration=".75s" />
        </MContainer>
    </MContainer>
</template>
<script lang="ts" setup>
import { MContainer, success } from "@xlabmedical/medic-web-framework";
import { ProjectFileType } from "@xlabmedical/medic-api";
import { CustomFile, usePatientsStore } from "@/stores/PatientsStore";
import { UniversalFilePicker } from "@xlabmedical/medic-web-engine";

enum UIState {
    INIT,
    SELECTED_OBJECT,
    UPLOADING,
}

const patientData = defineModel<{
    firstName: string | undefined;
    lastName: string;
    notes: string | undefined;
    dateOfBirth: string;
    extra: {
        patientName: string | undefined;
        study_instance_UID: string;
    };
}>("patientData", { required: false });
const props = defineProps<{
    patientName: string;
    createPatient: () => Promise<void>;
}>();

const emits = defineEmits<{ (e: "done", value: string): void }>();

const iuState = ref<UIState>(UIState.INIT);

const patientStore = usePatientsStore();

const accept = ref(".pdf,.txt,.zip");

const files = ref<File[]>([]);

const uploadProgress = ref(0);
const throttleUpdate = useThrottleFn((progress: number) => {
    uploadProgress.value = Math.ceil(progress * 100) - 1;
}, 250);

const importDocument = async () => {
    iuState.value = UIState.UPLOADING;
    if (patientData.value != undefined) {
        if (patientData.value.extra.study_instance_UID == "") {
            await props.createPatient();
        }
    }
    await Promise.all(
        files.value.map((file: File) => {
            patientStore.uploadFile(
                patientData.value ? patientData.value.extra.study_instance_UID : props.patientName,
                file.name,
                ProjectFileType.Document,
                file as CustomFile,
                (progress: number) => {
                    throttleUpdate(progress);
                },
            );
        }),
    );

    success(files.value.length > 1 ? "Documents uploaded successfully" : "Document uploaded successfully");
    removeFiles();
    iuState.value = UIState.INIT;
    emits("done", patientData.value ? patientData.value.extra.study_instance_UID : props.patientName);
};

const onUploadFiles = async (selection: File[]) => {
    iuState.value = UIState.SELECTED_OBJECT;
    files.value = selection;
    await importDocument();
};

const removeFiles = () => {
    files.value = [];
    iuState.value = UIState.INIT;
};
</script>

<style scoped>
.upload-container {
    position: relative;
}

.upload-overlay {
    position: absolute;
    inset: 0;
    background: rgba(100, 100, 100, 0.4);
    z-index: 2;
    border-radius: 16px;
}
</style>
